import { FC } from 'react';
import styles from './seat-info.module.scss';
import { classnames } from '@/utils/functions';

interface SeatInfoProps {
  seatsUsed: number;
  seatLimit: number;
  className?: string;
  loading?: boolean;
  compact?: boolean;
}

const SeatInfo: FC<SeatInfoProps> = ({
  seatsUsed,
  seatLimit,
  className = '',
  loading,
  compact = false,
}) => {
  if (loading) {
    return <div className={styles['seat-info']}>Loading seat info...</div>;
  }

  const seatsAvailable = Math.max(0, seatLimit - seatsUsed);
  const usagePercent =
    seatLimit > 0 ? Math.min(100, (seatsUsed / seatLimit) * 100) : 0;
  const isAtLimit = seatsAvailable === 0;
  const isNearLimit = seatLimit > 0 && usagePercent >= 80;
  const wrapperClass = classnames(styles['seat-info'], className);
  // Compact mode: minimal display for table cells
  if (compact) {
    return (
      <div
        className={classnames(wrapperClass, styles['compact'])}
        title={
          isAtLimit
            ? `${seatsUsed}/${seatLimit} - No seats available`
            : isNearLimit
              ? `${seatsUsed}/${seatLimit} - ${seatsAvailable} remaining`
              : `${seatsUsed}/${seatLimit} seats used`
        }
      >
        <span className={styles['seat-count-compact']}>
          {seatsUsed}/{seatLimit}
        </span>
        <div className={styles['seat-bar']}>
          <div
            className={styles['seat-bar-fill']}
            style={{ width: `${usagePercent}%` }}
            data-at-limit={isAtLimit}
            data-near-limit={isNearLimit}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={wrapperClass}>
      <div className={styles['seat-text']}>
        <span className={styles['seat-count']}>
          {seatsUsed} of {seatLimit} seats used
        </span>
        {isAtLimit && (
          <span className={styles['seat-warning']}>
            No seats available - new users will be blocked
          </span>
        )}
        {!isAtLimit && isNearLimit && (
          <span className={styles['seat-caution']}>
            {seatsAvailable} seat{seatsAvailable !== 1 ? 's' : ''} remaining
          </span>
        )}
      </div>
      <div className={styles['seat-bar']}>
        <div
          className={styles['seat-bar-fill']}
          style={{ width: `${usagePercent}%` }}
          data-at-limit={isAtLimit}
          data-near-limit={isNearLimit}
        />
      </div>
    </div>
  );
};

export default SeatInfo;
